import { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { buildCableModel } from '../../helpers/cableGeometry.js';
import CableCrossSection3D from '../../components/standard/CableCrossSection3D.js';
import ConductorIsolation3D from '../../components/standard/ConductorIsolation3D.js';
import Rope3D from '../../components/standard/Rope3D.js';
import SheathLayer3D from '../../components/standard/SheathLayer3D.js';
import Armour3D from '../../components/standard/Armour3D.js';
import PreStranding3D from '../../components/standard/PreStranding3D.js';
import { API } from '../../config/api';

const API_URL = `${API.BACKEND}/api/v1/standard`;
const getColorHex = colorName => {
  const map = {
    BLUE: '#007bff',
    BROWN: '#8B4513',
    BLACK: '#000000',
    GREY: '#808080',
    'GREEN-YELLOW': '#ADFF2F',
    GREEN: '#008000',
    YELLOW: '#FFD700'
  };
  return map[colorName.toUpperCase()] || '#cccccc'; // fallback
};

// the sheath sitting directly on the rope (bedding) — interior if present, else exterior
const beddingKeyOf = model => {
  if (!model?.hasRope) return null;
  const L = model.cableLayers.find(
    x => x.kind === 'sheath' && Math.abs(x.innerD - model.ropePackingD) < 0.3
  );
  return L?.key || null;
};

// the right 3D view for a phase, reflecting its real geometry (like Standard Analytics)
const Phase3D = ({ phase, model, beddingKey, productColor, height = 340 }) => {
  if (!model) return null;
  if (phase.name === 'conductor_isolation') return <ConductorIsolation3D model={model} height={height} />;
  if (phase.name === 'rope') return <Rope3D model={model} height={height} />;
  if (phase.name === 'interior_sheat' || phase.name === 'exterior_sheat') {
    const L = model.cableLayers.find(x => x.key === phase.name);
    if (!L) return null;
    return (
      <SheathLayer3D
        layer={L}
        productColor={productColor}
        bedding={phase.name === beddingKey && model.hasRope}
        cores={model.cores}
        numberOfConductors={model.numberOfConductors}
        ropeDiameter={model.ropeDiameter}
        flat={!model.hasRope && (model.numberOfConductors || model.cores.length) > 1}
        height={height}
      />
    );
  }
  if (phase.name === 'armouring') {
    const L = model.cableLayers.find(x => x.key === 'armouring');
    if (!L) return null;
    return <Armour3D layer={L} height={height} />;
  }
  if (phase.name === 'pre_stranding') {
    // pre_stranding fields are usually stored FLAT on the phase (type/numberOfWires/
    // wireDiameter/numberOfDrums/diameter); fall back to the phase when no rope[] array.
    const ropes = Array.isArray(phase.rope) && phase.rope.length ? phase.rope : [phase];
    if (!ropes.length) return null;
    return (
      <>
        {ropes.map((r, i) => (
          <div key={i} className={i ? 'mt-2' : ''}>
            <PreStranding3D rope={r} height={height} />
          </div>
        ))}
      </>
    );
  }
  return (
    <div style={{ height, background: '#0f1420', borderRadius: 6 }}
      className="d-flex align-items-center justify-content-center text-secondary small">
      Fără vizualizare 3D pentru această fază
    </div>
  );
};
const StandardOverview = () => {
  const { id } = useParams();
  const [standard, setStandard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  console.log(id);
  console.log(standard);
  useEffect(() => {
    const fetchStandard = async () => {
      try {
        setLoading(true);
        setError(null);

        // ✅ trimitem query params către backend
        const response = await axios.get(`${API_URL}/${id}`);
        console.log(`${API_URL}/${id}`);
        setStandard(response.data.data);
      } catch (err) {
        setError('Error fetching standard');
      } finally {
        setLoading(false);
      }
    };

    fetchStandard();
  }, []);

  const model = useMemo(
    () => (standard ? buildCableModel(standard, { length: 1000, layLength: 0 }) : null),
    [standard]
  );
  const beddingKey = useMemo(() => beddingKeyOf(model), [model]);

  if (loading) return <p>Loading...</p>;
  if (error) return <p>{error}</p>;
  if (!standard) return <p>No data available</p>;

  return (
    <div className="container-fluid mt-4">
      <h1 className="mb-4">{standard.name.toUpperCase()} Overview</h1>

      {/* Basic info + full-cable 3D side by side */}
      <div className="row g-3 align-items-start">
        <div className="col-lg-6">
          <StandardBasicInfoTable standard={standard} />
        </div>
        <div className="col-lg-6">
          <h3>Full cable — 3D</h3>
          {model && <CableCrossSection3D model={model} height={480} />}
        </div>
      </div>

      {/* Phases with per-phase 3D reflecting their real geometry */}
      <StandardPhasesTable
        phases={standard.phases}
        model={model}
        beddingKey={beddingKey}
        productColor={standard.color}
      />
    </div>
  );
};

// Display General Cable Information
const StandardBasicInfoTable = ({ standard }) => {
  return (
    <div className="table-responsive">
      <h3>Basic Information</h3>
      <table className="table table-bordered">
        <tbody>
          <tr>
            <th>Name</th>
            <td>{standard.name.toUpperCase()}</td>
          </tr>
          <tr>
            <th>Section</th>
            <td>
              {standard.section}
              {standard.hasMixedConductorSections && standard.greenYellowSection
                ? ` + ${standard.greenYellowSection}`
                : ''}{' '}
              mm²
            </td>
          </tr>
          <tr>
            <th>Number of Conductors</th>
            <td>{standard.numberOfConductors}</td>
          </tr>
          <tr>
            <th>Product Standard</th>
            <td>{standard.productStandard}</td>
          </tr>
          <tr>
            <th>Performance Standard</th>
            <td>{standard.performanceStandard}</td>
          </tr>
          <tr>
            <th>Notified Body</th>
            <td>{standard.notifiedBody}</td>
          </tr>
          <tr>
            <th>DOP</th>
            <td>{standard.dop}</td>
          </tr>
          <tr>
            <th>Tension</th>
            <td>{standard.tension}</td>
          </tr>
          <tr>
            <th>Fire Reaction</th>
            <td>{standard.fireReaction}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

// Display Phases in a Single Table with Bullet-Point Details
const StandardPhasesTable = ({ phases, model, beddingKey, productColor }) => {
  return (
    <div className="table mt-4">
      <h3>Production Phases</h3>
      <table className="table table-striped table-bordered">
        <thead className="table-dark">
          <tr>
            <th style={{ width: '14%' }}>Phase Name</th>
            <th>Details</th>
            <th style={{ width: '34%' }}>3D</th>
          </tr>
        </thead>
        <tbody>
          {phases.map((phase, index) => {
            const isCI = phase.name === 'conductor_isolation';
            return (
            <tr key={index}>
              <td>{phase.name.replace(/_/g, ' ').toUpperCase()}</td>
              <td colSpan={isCI ? 2 : 1}>
                <ul className="mb-0">
                  {/* Case: conductor_isolation with multiple conductors */}
                  {phase.name === 'pre_stranding' &&
                    (Array.isArray(phase.rope) ? (
                      <>
                        {phase.rope.map((ropeItem, idx) => (
                          <div key={idx} className="border rounded p-3 mb-3">
                            <h5>Copper Rope #{idx + 1}</h5>
                            <ul>
                              {Object.entries(ropeItem).map(([key, value]) => (
                                <li key={key}>
                                  <strong>
                                    {key
                                      .replace(/([A-Z])/g, ' $1')
                                      .toUpperCase()}
                                    :
                                  </strong>{' '}
                                  {Array.isArray(value)
                                    ? value.join(', ')
                                    : value}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </>
                    ) : (
                      // Dacă e un singur obiect
                      <div className="border rounded p-3 mb-3">
                        <h5>Pre-stranding</h5>
                        <ul>
                          {Object.entries(phase.rope || {}).map(
                            ([key, value]) => (
                              <li key={key}>
                                <strong>
                                  {key.replace(/([A-Z])/g, ' $1').toUpperCase()}
                                  :
                                </strong>{' '}
                                {Array.isArray(value)
                                  ? value.join(', ')
                                  : value}
                              </li>
                            )
                          )}
                        </ul>
                      </div>
                    ))}

                  {phase.name === 'conductor_isolation' &&
                  Array.isArray(phase.conductors) ? (
                    <>
                      {phase.conductors.map((conductor, idx) => (
                        <div key={idx} className="border rounded p-3 mb-3">
                          <h5>Conductor #{idx + 1}</h5>
                          <div className="row">
                            <div className="col-lg-7">
                              <div className="row">
                            <div className="col-md-6">
                              <h6>Wire Details</h6>
                              <ul>
                                <li>
                                  <strong>Material:</strong>{' '}
                                  {conductor.wire.material}
                                </li>
                                <li>
                                  <strong>Type:</strong> {conductor.wire.type}
                                </li>
                                <li>
                                  <strong>Density:</strong>{' '}
                                  {conductor.wire.density} g/cm³
                                </li>
                                {conductor.wire.numberOfWires && (
                                  <li>
                                    <strong>Number of Wires:</strong>{' '}
                                    {Array.isArray(conductor.wire.wireDiameter)
                                      ? conductor.wire.numberOfWires.join(', ')
                                      : conductor.wire.numberOfWires}{' '}
                                  </li>
                                )}
                                {conductor.wire.wireDiameter && (
                                  <li>
                                    <strong>Wire Diameter:</strong>{' '}
                                    {Array.isArray(conductor.wire.wireDiameter)
                                      ? conductor.wire.wireDiameter.join(', ')
                                      : conductor.wire.wireDiameter}{' '}
                                    mm
                                  </li>
                                )}
                                <li>
                                  <strong>Diameter:</strong>{' '}
                                  {conductor.wire.diameter} mm
                                </li>
                                <li>
                                  <strong>Section:</strong>{' '}
                                  {conductor.wire.section} mm²
                                </li>
                              </ul>
                            </div>
                            <div className="col-md-6">
                              <h6>Isolation Details</h6>
                              <ul>
                                <li>
                                  <strong>Material:</strong>{' '}
                                  {conductor.isolation.material}
                                </li>
                                <li>
                                  <strong>Type:</strong>{' '}
                                  {conductor.isolation.type}
                                </li>
                                <li>
                                  <strong>Density:</strong>{' '}
                                  {conductor.isolation.density} g/cm³
                                </li>
                                <li>
                                  <strong>Diameter:</strong>{' '}
                                  {conductor.isolation.diameter} mm
                                </li>
                                <li>
                                  <strong>Section:</strong>{' '}
                                  {conductor.isolation.section} mm²
                                </li>
                                <li>
                                  <strong>Filling Section:</strong>{' '}
                                  {conductor.isolation.fillingSection} mm²
                                </li>
                                <li>
                                  <strong>Min Radial:</strong>{' '}
                                  {conductor.isolation.minRadial} mm
                                </li>
                                <li>
                                  <strong>Max Radial:</strong>{' '}
                                  {conductor.isolation.maxRadial} mm
                                </li>
                                {conductor.isolation.color && (
                                  <li className="d-flex align-items-center gap-2">
                                    <strong>Color:</strong>
                                    <span
                                      style={{
                                        backgroundColor: getColorHex(
                                          conductor.isolation.color
                                        ),
                                        width: '20px',
                                        height: '20px',
                                        display: 'inline-block',
                                        borderRadius: '50%',
                                        border: '1px solid #555'
                                      }}
                                      title={conductor.isolation.color}
                                    />
                                    <span>{conductor.isolation.color}</span>
                                  </li>
                                )}
                              </ul>
                            </div>
                              </div>
                            </div>
                            <div className="col-lg-5">
                              {model?.cores?.[idx] && (
                                <ConductorIsolation3D
                                  model={{ cores: [model.cores[idx]] }}
                                  height={280}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  ) : (
                    <>
                      {/* Wire and Isolation for single-object phases */}
                      <div className="row">
                        <div className="col-md-6">
                          {phase.wire && (
                            <>
                              <h5>Wire Details</h5>
                              <ul>
                                <li>
                                  <strong>Material:</strong>{' '}
                                  {phase.wire.material}
                                </li>
                                <li>
                                  <strong>Type:</strong> {phase.wire.type}
                                </li>
                                <li>
                                  <strong>Density:</strong> {phase.wire.density}{' '}
                                  g/cm³
                                </li>
                                {phase.wire.numberOfWires && (
                                  <li>
                                    <strong>Number of Wires:</strong>{' '}
                                    {Array.isArray(phase.wire.numberOfWires)
                                      ? phase.wire.numberOfWires.join(', ')
                                      : phase.wire.numberOfWires}
                                  </li>
                                )}
                                {phase.wire.wireDiameter && (
                                  <li>
                                    <strong>Wire Diameter:</strong>{' '}
                                    {Array.isArray(phase.wire.wireDiameter)
                                      ? phase.wire.wireDiameter.join(', ')
                                      : phase.wire.wireDiameter}{' '}
                                    mm
                                  </li>
                                )}
                                <li>
                                  <strong>Diameter:</strong>{' '}
                                  {phase.wire.diameter} mm
                                </li>
                                <li>
                                  <strong>Section:</strong> {phase.wire.section}{' '}
                                  mm²
                                </li>
                              </ul>
                            </>
                          )}
                        </div>

                        <div className="col-md-6">
                          {phase.isolation && (
                            <>
                              <h5>Isolation Details</h5>
                              <ul>
                                <li>
                                  <strong>Material:</strong>{' '}
                                  {phase.isolation.material}
                                </li>
                                <li>
                                  <strong>Type:</strong> {phase.isolation.type}
                                </li>
                                <li>
                                  <strong>Density:</strong>{' '}
                                  {phase.isolation.density} g/cm³
                                </li>
                                <li>
                                  <strong>Diameter:</strong>{' '}
                                  {phase.isolation.diameter} mm
                                </li>
                                <li>
                                  <strong>Section:</strong>{' '}
                                  {phase.isolation.section} mm²
                                </li>
                                <li>
                                  <strong>Filling Section:</strong>{' '}
                                  {phase.isolation.fillingSection} mm²
                                </li>
                                <li>
                                  <strong>Min Radial:</strong>{' '}
                                  {phase.isolation.minRadial} mm
                                </li>
                                <li>
                                  <strong>Max Radial:</strong>{' '}
                                  {phase.isolation.maxRadial} mm
                                </li>
                                {phase.isolation.color && (
                                  <li>
                                    <strong>Color:</strong>{' '}
                                    {phase.isolation.color}
                                  </li>
                                )}
                              </ul>
                            </>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Other raw attributes */}
                  {Object.entries(phase)
                    .filter(
                      ([key]) =>
                        ![
                          'wire',
                          'isolation',
                          'conductors',
                          'name',
                          'rope'
                        ].includes(key)
                    )
                    .map(([key, value], i) => (
                      <li key={i}>
                        <strong>{key.replace(/_/g, ' ').toUpperCase()}:</strong>{' '}
                        {typeof value === 'object'
                          ? JSON.stringify(value)
                          : value}
                      </li>
                    ))}
                </ul>
              </td>
              {!isCI && (
                <td style={{ verticalAlign: 'top' }}>
                  <Phase3D
                    phase={phase}
                    model={model}
                    beddingKey={beddingKey}
                    productColor={productColor}
                  />
                </td>
              )}
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default StandardOverview;
